import { useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ToastType = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastProps {
  toast: Toast;
  onClose: (id: string) => void;
}

function ToastItem({ toast, onClose }: ToastProps) {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const duration = toast.duration || 5000;
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => onClose(toast.id), 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onClose]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => onClose(toast.id), 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  const icon = {
    success: <CheckCircle className="h-5 w-5 text-green-600" aria-hidden="true" />,
    error: <AlertCircle className="h-5 w-5 text-red-600" aria-hidden="true" />,
    info: <Info className="h-5 w-5 text-blue-600" aria-hidden="true" />,
  }[toast.type];

  const bgColor = {
    success: 'bg-green-50 border-green-200',
    error: 'bg-red-50 border-red-200',
    info: 'bg-blue-50 border-blue-200',
  }[toast.type];

  const textColor = {
    success: 'text-green-900',
    error: 'text-red-900',
    info: 'text-blue-900',
  }[toast.type];

  // Use assertive for errors, polite for everything else
  const ariaLive = toast.type === 'error' ? 'assertive' : 'polite';

  return (
    <div
      role="status"
      aria-live={ariaLive}
      aria-atomic="true"
      className={cn(
        'flex items-start gap-3 rounded-lg border p-4 shadow-lg transition-all duration-300',
        bgColor,
        isExiting ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'
      )}
      onKeyDown={handleKeyDown}
    >
      {icon}
      <p className={cn('flex-1 text-sm font-medium', textColor)}>
        {toast.message}
      </p>
      <button
        onClick={handleClose}
        aria-label="Close notification"
        className={cn(
          'rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2',
          toast.type === 'success' && 'focus:ring-green-600',
          toast.type === 'error' && 'focus:ring-red-600',
          toast.type === 'info' && 'focus:ring-blue-600'
        )}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

interface ToastContainerProps {
  toasts: Toast[];
  onClose: (id: string) => void;
}

export function ToastContainer({ toasts, onClose }: ToastContainerProps) {
  return (
    <div
      className="fixed top-4 right-4 z-50 flex flex-col gap-2 w-full max-w-md pointer-events-none"
      aria-label="Notifications"
    >
      <div className="pointer-events-auto space-y-2">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onClose={onClose} />
        ))}
      </div>
    </div>
  );
}
